import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";

import { asDragSource } from "../../components/dragAndDrop/DraggableComponent";
import FormRadio from "../../components/form/FormRadio";
import { ITEM_CARD_LIST_VIEW } from "../../constants/identifiers";
import tableDropAction from "../../constants/tableDropAction";
import { PropTypeShapes } from "../../types";
import hasAncestorOfClass from "../../utils/hasAncestorOfClass";
import { withContainerContext } from "../container/ContainerContext";
import Icon from "../interface/Icon";
import ItemCardView from "../itemCardView/ItemCardView";

const clickWasDragHandle = e => hasAncestorOfClass(e.target, "dragHandle");

const dragCallbacks = {
  beginDrag: _props => {
    // TODO: Do we want to set isDragging or similar...?
  },

  canDrag: (props, _dragSource, e) => {
    return clickWasDragHandle(e) && props.canDrag && props.type;
  },

  getDragSourceParams: props => {
    return {
      itemId: props.id,
      itemType: props.type,
      sourceView: props.viewId,
      sourceViewType: ITEM_CARD_LIST_VIEW
    };
  },

  getPreviewElement: props => {
    return renderItem(props, true);
  }
};

const getCollapseArrow = itemProps => {
  const icon = itemProps.hasChildren ? (itemProps.childrenExpanded ? "downArrow" : "rightArrow") : "";
  return itemProps.hasChildren ? (
    <Icon
      key="collapseArrow"
      icon={icon}
      className="collapseIcon"
      color="iconMono"
      onClick={e => {
        if (itemProps.onToggleChildren) {
          itemProps.onToggleChildren(itemProps.id, !itemProps.childrenExpanded);
        }
        e.stopPropagation();
      }}
    />
  ) : null;
};

const getDragHandle = itemProps => {
  const icon = itemProps.canDrag ? "dragHandle" : "";
  return <Icon key="dragHandle" className="dragHandle" icon={icon} color="iconMid" />;
};

const renderItem = (itemProps, isPreview = false) => {
  let classes = ["itemCardRow"];
  if (itemProps.selected) {
    classes.push("selected");
  }
  if (itemProps.isEditMode) {
    classes.push("editMode");
  }

  if (itemProps.drawDropAction === tableDropAction.DropAboveRow) {
    classes.push("dropAbove");
  } else if (itemProps.drawDropAction === tableDropAction.DropBelowRow) {
    classes.push("dropBelow");
  } else if (itemProps.drawDropAction === tableDropAction.DropOnRow) {
    classes.push("dropOn");
  }

  if (itemProps.index % 2) {
    classes.push("odd");
  }
  let styles = {
    transform: `translateY(${itemProps.rect.top}px)`,
    height: `${itemProps.rect.height}px`,
    width: `${itemProps.containerRect.width}px`
  };
  if (isPreview) {
    styles.transform = "none";
    styles.position = "relative";
  }

  const selectRadio = itemProps.isEditMode ? <FormRadio isChecked={itemProps.selected} onChange={() => {}} /> : null;

  const dragHandle = getDragHandle(itemProps);

  const eventDot = <div className={`itemCardEventDot ${itemProps.color}`} />;

  const collapseArrow = getCollapseArrow(itemProps);

  const rightElements = itemProps.isEditMode ? [collapseArrow, dragHandle] : [dragHandle, collapseArrow];

  return (
    <div className={classes.join(" ")} style={styles}>
      <div
        className={`eventCol indent${itemProps.indentationLevel} ${itemProps.hasChildren ? "hasChildren" : ""}`}
        onClick={_e => itemProps.onClick(itemProps.id)}
      >
        {itemProps.selected ? selectRadio : eventDot}
        <div className="eventColText">
          <ItemCardView
            {...itemProps.itemCardViewSettings.toObject()}
            rect={{ width: itemProps.rect.width }}
            titleMetrics={itemProps.titleMetrics}
            dateTextSize={itemProps.dateTextSize}
            narrativeTextSize={itemProps.narrativeTextSize}
            durationTextSize={itemProps.durationTextSize}
            summaryMetrics={itemProps.summaryMetrics}
            identifierSize={itemProps.identifierSize}
            identifier={itemProps.displayId}
            relatedItemsByType={itemProps.relatedItemsByType}
            blockingIdentifiers={itemProps.blockingIdentifiers}
            blockedIdentifiers={itemProps.blockedIdentifiers}
            imagePath={itemProps.imagePath}
            autoEditWhenBlank={false}
            hasViolatedConstraints={itemProps.hasViolatedConstraints}
            isCompleted={itemProps.isCompleted}
            typesById={itemProps.typesById}
            type={itemProps.type}
          />
        </div>
        {rightElements}
      </div>
    </div>
  );
};

class ItemCardRowView extends PureComponent {
  render() {
    return renderItem(this.props, this.props.isPreview);
  }
}

ItemCardRowView.propTypes = {
  viewId: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  onToggleChildren: PropTypes.func,
  selected: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired,
  rect: PropTypeShapes.flatRect.isRequired,
  fontSize: PropTypes.number.isRequired,

  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  displayId: PropTypes.string.isRequired,
  titleMetrics: PropTypeShapes.textMetrics.isRequired,
  dateTextSize: PropTypeShapes.textSize.isRequired,
  durationTextSize: PropTypeShapes.textSize.isRequired,
  narrativeTextSize: PropTypeShapes.textSize.isRequired,
  summaryMetrics: PropTypeShapes.textMetrics.isRequired,
  identifierSize: PropTypeShapes.size.isRequired,
  relatedItemsByType: ImmutablePropTypes.list.isRequired,
  blockingIdentifiers: PropTypeShapes.identifierList.isRequired,
  blockedByIdentifiers: PropTypeShapes.identifierList.isRequired,
  imagePath: PropTypes.string,

  hasChildren: PropTypes.bool.isRequired,
  childrenExpanded: PropTypes.bool.isRequired,
  indentationLevel: PropTypes.number.isRequired,

  itemCardViewSettings: PropTypeShapes.itemCardViewSettings.isRequired,

  typesById: ImmutablePropTypes.map.isRequired,

  canDrag: PropTypes.bool,
  isDraggingRow: PropTypes.bool,
  drawDropAction: PropTypes.string,
  isEditMode: PropTypes.bool,

  // From containerContext
  containerRect: PropTypeShapes.flatRect.isRequired,

  // From asDragSource
  isDragging: PropTypes.bool,
  dragIsFromSameSource: PropTypes.bool,
  isPreview: PropTypes.bool
};

export default withContainerContext(asDragSource(ItemCardRowView, ITEM_CARD_LIST_VIEW, dragCallbacks));
